import { Shimmer } from "@/components/ai-elements/shimmer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChatPeopleCardsSheet } from "@/components/chat/chat-people-cards-sheet";
import {
  PeopleToolCard,
  PeopleToolNotice,
} from "@/components/chat/chat-people-tool-card";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { readPeopleToolOutput } from "@/lib/people-table";
import { cn } from "@/lib/utils";
import { useLivePeopleTablePanel } from "@/hooks/useLivePeopleTablePanel";
import {
  chatArtifactPanelStore,
  useShownPeopleSearchId,
} from "@/stores/chat-artifact-panel-store";
import {
  SearchPeopleToolDone,
  SearchPeopleToolOutput,
  SearchPeopleToolRunning,
} from "@shaarp/common";
import { ToolUIPart } from "ai";
import {
  AlertCircleIcon,
  Loader2Icon,
  SearchXIcon,
  TableIcon,
  UsersRoundIcon,
} from "lucide-react";
import { useState } from "react";

/** The query the model is writing into the call, before any output. */
const readInputQuery = (input: unknown): string | null => {
  const query = (input as { query?: unknown } | undefined)?.query;
  return typeof query === "string" ? query : null;
};

/**
 * A `searchPeople` call in the thread. While the search runs it narrates the
 * wait and fills the table beside the conversation; once done, it is one line
 * — how many people the table holds — and the way to show or hide it.
 *
 * The panel only opens for a run watched live: reopening a conversation
 * replays finished calls, which must not pop an old table open. On a phone,
 * which has no room for the table, the card opens its people as contact
 * cards in a sheet instead.
 */
export function ChatToolSearchPeople({
  part,
  isLive,
  onSendMessage,
}: {
  part: ToolUIPart;
  /** Whether the answer holding the call is still streaming. */
  isLive: boolean;
  /** Sends a follow-up in the conversation, from a contact card's actions. */
  onSendMessage?: (text: string) => void;
}) {
  const { isMobile } = useDeviceFormat();
  const canShowTable = !isMobile;

  const output =
    part.state === "output-available"
      ? readPeopleToolOutput<SearchPeopleToolOutput>(part.output)
      : null;
  const running: SearchPeopleToolRunning | null =
    output?.status === "running" ? output : null;
  const done: SearchPeopleToolDone | null =
    output?.status === "done" ? output : null;
  // A progress output on an answer no longer streaming is a call that was
  // cut mid-run: what it had found is in the table, nothing more is coming.
  const isRunningLive = running !== null && isLive;

  const peopleCount = done?.peopleCount ?? running?.peopleCount ?? 0;
  useLivePeopleTablePanel({
    running,
    isLive,
    canShowTable,
    isSettled:
      part.state === "output-error" ||
      (part.state === "output-available" && !isRunningLive),
    hasPeople: peopleCount > 0,
  });

  if (part.state === "output-error") {
    return <SearchPeopleFailed />;
  }

  if (output?.status === "refused") {
    // Every other refusal is the model's to correct, and it does so in the
    // same turn: a marker per attempt would be noise. A search that broke is
    // worth saying, the answer explains it.
    return output.reason === "failed" ? <SearchPeopleFailed /> : null;
  }

  if (!output || isRunningLive) {
    // Before the first output, the call is still being written; after the
    // answer stopped, a call without one never ran.
    if (!output && !isLive) {
      return <SearchPeopleFailed />;
    }
    return (
      <SearchPeopleRunning
        running={running}
        query={running?.query ?? readInputQuery(part.input)}
      />
    );
  }

  // Settled: a finished search, or one cut in the middle of its run.
  return (
    <SearchPeopleSettled
      done={done}
      running={running}
      peopleCount={peopleCount}
      canShowTable={canShowTable}
      onSendMessage={onSendMessage}
    />
  );
}

/** The search at work: what it is doing, and how many it found so far. */
function SearchPeopleRunning({
  running,
  query,
}: {
  running: SearchPeopleToolRunning | null;
  query: string | null;
}) {
  const { t } = useTranslation();
  return (
    <PeopleToolCard aria-live="polite" data-state="running">
      <div className="flex gap-2 items-center min-w-0">
        <Loader2Icon className="size-4 shrink-0 animate-spin text-primary motion-reduce:animate-none" />
        <span className="text-sm text-muted-foreground shrink-0">
          {t("chat.peopleSearch.searching")}
        </span>
        {running && running.peopleCount > 0 && (
          <Badge variant="majorelle" className="tabular-nums">
            {t("chat.peopleSearch.found", { count: running.peopleCount })}
          </Badge>
        )}
      </div>
      {(running?.liveStatus || query) && (
        <Shimmer className="text-xs truncate">
          {running?.liveStatus ?? query ?? ""}
        </Shimmer>
      )}
    </PeopleToolCard>
  );
}

/**
 * A search that ended, finished or cut mid-run: how many people the table
 * holds, and the way to show or hide it — on a phone, to open its people.
 */
function SearchPeopleSettled({
  done,
  running,
  peopleCount,
  canShowTable,
  onSendMessage,
}: {
  done: SearchPeopleToolDone | null;
  running: SearchPeopleToolRunning | null;
  peopleCount: number;
  canShowTable: boolean;
  onSendMessage?: (text: string) => void;
}) {
  const { t } = useTranslation();
  const arePeopleVisible = useArePeopleVisible();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const searchId = done?.searchId ?? running?.searchId ?? "";
  const query = done?.query ?? running?.query;
  // Which table is shown, not the panel: every card of the thread would
  // otherwise render again on each row a search streams.
  const isShown = useShownPeopleSearchId() === searchId;
  const togglePanel = (): void => {
    if (isShown) chatArtifactPanelStore.close();
    else chatArtifactPanelStore.openPeopleTable(searchId);
  };

  return (
    <PeopleToolCard
      className={cn(
        "transition-[border-color] duration-150",
        // Marked by its border and a soft ring, as a shown document's card.
        isShown && "border-primary/50 ring-2 ring-primary/10",
      )}
      data-state="done"
    >
      <div className="flex gap-2 items-center min-w-0">
        {peopleCount > 0 ? (
          <UsersRoundIcon className="size-4 shrink-0 text-primary" />
        ) : (
          <SearchXIcon className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-medium shrink-0">
          {peopleCount > 0
            ? t("chat.peopleSearch.result", { count: peopleCount })
            : t("chat.peopleSearch.empty")}
        </span>
        <span className="text-sm truncate text-muted-foreground">
          {query}
        </span>
        {canShowTable && peopleCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto shrink-0"
            aria-expanded={isShown}
            onClick={togglePanel}
          >
            <TableIcon />
            {isShown
              ? t("chat.peopleSearch.hideTable")
              : t("chat.peopleSearch.showTable")}
          </Button>
        )}
      </div>
      {/* On a phone, which has no room for the table, its people open as
          contact cards in a sheet. A team that has since cut its contacts
          has none to open. */}
      {!canShowTable && peopleCount > 0 && arePeopleVisible && (
        <>
          <Button
            variant="outline"
            size="lg"
            className="mt-1 w-full"
            onClick={() => setIsSheetOpen(true)}
          >
            <UsersRoundIcon />
            {t("chat.peopleSearch.showPeople")}
          </Button>
          <ChatPeopleCardsSheet
            open={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            searchId={searchId}
            query={query}
            peopleCount={peopleCount}
            onSendMessage={onSendMessage}
          />
        </>
      )}
      {/* Stated by the card rather than left to the answer: a table of 100
          on a request for 150 otherwise reads as a search that came up short. */}
      {done?.perSearchLimit !== undefined && (
        <PeopleToolNotice>
          {t("chat.peopleSearch.cappedNotice", {
            requested: done.requestedCount,
            limit: done.perSearchLimit,
          })}
        </PeopleToolNotice>
      )}
      {done?.wasCutShort && (
        <PeopleToolNotice>{t("chat.peopleSearch.cutShortNotice")}</PeopleToolNotice>
      )}
      {done?.wasInterrupted && (
        <PeopleToolNotice>{t("chat.peopleSearch.readFailedNotice")}</PeopleToolNotice>
      )}
      {running && (
        <PeopleToolNotice>{t("chat.peopleSearch.interruptedNotice")}</PeopleToolNotice>
      )}
    </PeopleToolCard>
  );
}

/** The search that could not run, or broke: nothing to open. */
function SearchPeopleFailed() {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 items-center p-3 mb-2 w-full rounded-xl border border-dashed not-prose">
      <AlertCircleIcon className="size-4 shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">
        {t("chat.peopleSearch.failed")}
      </span>
    </div>
  );
}
